import Dialog from '@src/components/dialog'
import React, { useState } from 'react'
import { segmentTrack } from '@src/functions/segment'
import { useRouter } from 'next/router'
import submitPPERequest from '@src/functions/request-ppe-quote'
import classes from '../styles/QuoteRequestForm.module.css'

const DescriptionForm = ({
  open,
  onClose,
}: {
  open: boolean
  onClose: (...args: Array<any>) => any
}) => {
  const history = useRouter()
  const [isBusiness, setIsBusiness] = useState<string>('Yes')
  const [companyName, setCompanyName] = useState<string>('')
  const [customerName, setCustomerName] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [phoneNumber, setPhoneNumber] = useState<string>('')
  const [zipCode, setZipCode] = useState<string>('')
  const [pallets, setPallets] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [materials, setMaterials] = useState<Array<string>>([])
  const [submitting, setSubmitting] = useState(false)

  const handleMaterialChange = (e: any) => {
    const material = e.target.value
    const isSelected = e.target.checked

    if (isSelected) {
      setMaterials([...materials, material])
    } else {
      const updatedMaterials = materials.filter((item: any) => item !== material)
      setMaterials(updatedMaterials)
    }
  }

  // `Contact Entered` is a funnel signal: it marks that the visitor started
  // filling in contact details, and which fields they have completed so far. It
  // deliberately carries no name, email, phone or company name — this fires on
  // every field blur, so sending those would scatter identifying data across
  // analytics for a form the visitor may never submit. The real values still go
  // to the backend on submit, which is the only place they are needed.
  const handleBlur = (e: any) => {
    if (e.target.value) {
      segmentTrack('Contact Entered', {
        field: e.target.id,
        hasName: Boolean(customerName),
        hasEmail: Boolean(email),
        hasCompanyName: Boolean(companyName),
        hasPhone: Boolean(phoneNumber),
        hasZipCode: Boolean(zipCode),
        from: 'PPE Form',
        site: 'NextJS',
      })
    }
  }

  const handleSubmit = async (e: any) => {
    e.preventDefault()
    setSubmitting(true)
    const formData = {
      isBusiness: isBusiness === 'Yes',
      companyName,
      customerName,
      email,
      phoneNumber,
      zipCode,
      materials,
      pallets: Number(pallets),
      description,
      pathname: history.pathname,
    }
    const { success } = await submitPPERequest(formData)
    if (success) {
      segmentTrack('PPE Quote Request Submitted', formData)
      history.push('/ppequote_success')
    } else {
      segmentTrack('PPE Quote Request Failed', formData)
      // eslint-disable-next-line no-alert
      alert('Please make sure all fields are filled out correctly!')
      setSubmitting(false)
    }
  }

  return (
    <Dialog show={open} onHide={onClose}>
      <div className={classes.container}>
        <div className={classes.content}>
          <h3 className={classes.header}>
            Hand Sanitizer and PPE removal price quote - Dimension Waste Management
          </h3>
          <form className={classes.form} onSubmit={handleSubmit} autoComplete='on'>
            <div className={classes.complexFormField}>
              <span style={{ marginBottom: 5 }} className={classes.complexFormFieldLabel}>
                Are you a business or public sector customer that has at least 1 full pallet of PPE
                material to be removed?
                <span className={classes.textRed} aria-hidden='true' role='presentation'>
                  *
                </span>
              </span>
              <span className={classes.smallText}>
                Note that, if you are a Consumer or Residential customer or if you don&apos;t have
                much PPE material to remove , please simply go through{' '}
                <a href='https://getdimension.com'>getdimension.com</a> and click the &quot;
                <strong>Book Now</strong>&quot; button to book your service instantly!
              </span>
              <div className={classes.complexFormFieldsContainer} style={{ marginBottom: 8 }}>
                <label
                  className={`${classes.formFieldRadio} ${classes.greenLight}`}
                  htmlFor='isBusiness'
                >
                  Yes!
                  <input
                    className={classes.input}
                    type='radio'
                    id='isBusiness'
                    name='isBusiness'
                    value='Yes!'
                    onChange={() => setIsBusiness('Yes')}
                    required
                  />
                </label>
                <label
                  className={`${classes.formFieldRadio} ${classes.orangeLight}`}
                  htmlFor='isBusiness'
                >
                  No
                  <input
                    className={classes.input}
                    type='radio'
                    id='isBusiness'
                    name='isBusiness'
                    value='Yes!'
                    onChange={() => setIsBusiness('No')}
                    required
                  />
                </label>
              </div>
            </div>
            <label className={classes.formField} htmlFor='businessName'>
              What&apos;s the name of your business?
              {isBusiness === 'Yes' && (
                <span className={classes.textRed} aria-hidden='true' role='presentation'>
                  *
                </span>
              )}
              <input
                className={classes.input}
                type='text'
                id='companyName'
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                onBlur={handleBlur}
                required={isBusiness === 'Yes'}
                autoComplete='organization'
              />
            </label>
            <label className={classes.formField} htmlFor='customerName'>
              What&apos;s your name?
              <span className={classes.textRed} aria-hidden='true' role='presentation'>
                *
              </span>
              <input
                className={classes.input}
                type='text'
                id='customerName'
                placeholder='John Doe'
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                onBlur={handleBlur}
                required
                autoComplete='name'
              />
            </label>
            <label className={classes.formField} htmlFor='email'>
              What&apos;s your work email address?
              <span className={classes.textRed} aria-hidden='true' role='presentation'>
                *
              </span>
              <input
                className={classes.input}
                type='email'
                id='email'
                placeholder='example@example.com'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={handleBlur}
                required
              />
            </label>
            <label className={classes.formField} htmlFor='phoneNumber'>
              What is the best phone number to reach you?
              <span className={classes.textRed} aria-hidden='true' role='presentation'>
                *
              </span>
              <input
                className={classes.input}
                type='tel'
                id='phoneNumber'
                placeholder='(123) 456-7890'
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                onBlur={handleBlur}
                required
              />
            </label>
            <label className={classes.formField} htmlFor='zipCode'>
              Please enter the zip code of the service address.
              <span className={classes.textRed} aria-hidden='true' role='presentation'>
                *
              </span>
              <input
                className={classes.input}
                type='text'
                id='zipCode'
                placeholder='12345'
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                onBlur={handleBlur}
                required
                autoComplete='postal-code'
              />
            </label>
            <div className={classes.complexFormField}>
              <span className={classes.complexFormFieldLabel}>
                Please select all the materials you wish to remove and receive a price quote for:
              </span>
              <div className={classes.complexFormFieldsContainer}>
                <label
                  className={`${classes.formField} ${classes.blueLight}`}
                  htmlFor='handSanitizer'
                >
                  Hand Sanitizer
                  <input
                    type='checkbox'
                    id='handSanitizer'
                    value='Hand Sanitizer'
                    onChange={handleMaterialChange}
                    checked={materials.includes('Hand Sanitizer')}
                  />
                </label>
                <label className={`${classes.formField} ${classes.cyanLight}`} htmlFor='masks'>
                  Masks
                  <input
                    type='checkbox'
                    id='masks'
                    value='Masks'
                    onChange={handleMaterialChange}
                    checked={materials.includes('Masks')}
                  />
                </label>
                <label
                  className={`${classes.formField} ${classes.tealLight}`}
                  htmlFor='faceShields'
                >
                  Face shields
                  <input
                    type='checkbox'
                    id='faceShields'
                    value='Face shields'
                    onChange={handleMaterialChange}
                    checked={materials.includes('Face shields')}
                  />
                </label>
                <label className={`${classes.formField} ${classes.greenLight}`} htmlFor='detergent'>
                  Detergent
                  <input
                    type='checkbox'
                    id='detergent'
                    value='Detergent'
                    onChange={handleMaterialChange}
                    checked={materials.includes('Detergent')}
                  />
                </label>
                <label
                  className={`${classes.formField} ${classes.yellowLight}`}
                  htmlFor='otherPPEs'
                >
                  Other PPEs
                  <input
                    type='checkbox'
                    id='otherPPEs'
                    value='Other PPEs'
                    onChange={handleMaterialChange}
                    checked={materials.includes('Other PPEs')}
                  />
                </label>
                <label
                  className={`${classes.formField} ${classes.orangeLight}`}
                  htmlFor='nonPPEWaste'
                >
                  I also have non-PPE waste to remove
                  <input
                    type='checkbox'
                    id='nonPPEWaste'
                    value='Non-PPE Waste'
                    onChange={handleMaterialChange}
                    checked={materials.includes('Non-PPE Waste')}
                  />
                </label>
              </div>
            </div>
            <label className={classes.formField} htmlFor='pallets'>
              In total, how many pallets of materials do you wish to remove and receive a price
              quote? Please enter a number below.
              <input
                className={classes.input}
                type='number'
                id='pallets'
                placeholder='eg: 0'
                value={pallets}
                onChange={(e) => setPallets(e.target.value)}
              />
            </label>
            <label className={classes.formField} htmlFor='description'>
              In one or two sentences, please briefly describe your quote request.
              <textarea
                className={classes.input}
                id='description'
                placeholder='eg: I have 10 pallets of hand sanitizer to remove.'
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </label>

            <button className={classes.submitButton} type='submit' disabled={submitting}>
              {submitting ? 'Submitting' : 'Submit'}
            </button>
          </form>
        </div>
      </div>
    </Dialog>
  )
}

export default DescriptionForm
